import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useFeathers } from '../../app/util';
import { useSelector } from 'react-redux';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { DataDetailToolbar, FilterButton, LoadingSpinner, DistrictSelectInput, StepIndicator } from '../../components';
import moment from 'moment';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { District } from '../../app/feathers/districts/District';
import { AutoScouter } from '../../app/feathers/auto-scouter/AutoScouter';
import { Election } from '../../app/feathers/elections/Election';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  AutoScouterListState,
  autoScouterStateOptions,
  getAutoScouterStateQuery,
  getAutoScouterSteps,
  hasCreateError,
} from './getAutoScouterSteps';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

type RootState = {
  elections?: {
    byKey: Record<string, Election | undefined>;
  };
};

type AutoScouterFindQuery = {
  electionKey: string;
  $sort: { requestedAt: -1 };
  $limit: number;
  $skip: number;
} & Record<string, any>;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 8px;
  border: 1px #EEEEEE solid;
  overflow: hidden;
  background: #FFFFFF;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 12px;
  border-bottom: 1px solid #EEEEEE;
  background: #FFFFFF;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 250px 1fr 1.5fr;
  grid-gap: 24px;
  padding: 16px 24px;
  background: #F7F9FE;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;

  h5 {
    margin: 0;
    font-weight: bold;
    color: #313639;
  }
`;

const TableRowLink = styled(Link)`
  display: grid;
  grid-template-columns: 1.5fr 250px 1fr 1.5fr;
  grid-gap: 24px;
  padding: 16px 24px;
  border-bottom: 1px solid #EEEEEE;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    background-color: #F7F9FE;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LoadingTile = styled.div`
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;

  span {
    margin-bottom: 20px;
  }
`;

const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
`;

const CreateErrorWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: #ed6c02;
  font-size: 11px;
  line-height: 1.25;
`;

const AutoScouters = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const { key: electionKey } = useParams<{ key: string }>();
  const history = useHistory();
  const location = useLocation();
  const electionData = useSelector((state: RootState) => state.elections?.byKey[electionKey]);
  
  const [autoScouters, setAutoScouters] = useState<AutoScouter[]>([]);
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState<District[]>([]);
  const [total, setTotal] = useState<number>(0);

  // URL-sourced state
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  
  const initialState = useMemo(() => {
    const raw = searchParams.get('state');
    return autoScouterStateOptions.some(option => option.value === raw)
      ? raw as AutoScouterListState
      : null;
  }, [searchParams]);
  const initialPage = useMemo(() => {
    const p = Number(searchParams.get('page'));
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [searchParams]);
  const initialPageSize = useMemo(() => {
    const s = Number(searchParams.get('pageSize'));
    return Number.isFinite(s) && s > 0 ? s : 25;
  }, [searchParams]);

  const [selectedState, setSelectedState] = useState<AutoScouterListState | null>(initialState);
  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  
  // Store previous autoScouters for comparison
  const previousAutoScoutersRef = useRef<AutoScouter[]>([]);

  // Check if there are meaningful status changes between old and new autoScouters
  const hasStatusChanges = useCallback((oldAutoScouters: AutoScouter[], newAutoScouters: AutoScouter[]): boolean => {
    // Create a map of old autoScouters by _id for quick lookup
    const oldMap = new Map<string, AutoScouter>();
    oldAutoScouters.forEach(as => {
      if (as._id) oldMap.set(as._id, as);
    });

    // Check each new autoScouter for changes
    for (const newAs of newAutoScouters) {
      if (!newAs._id) continue;
      
      const oldAs = oldMap.get(newAs._id);
      
      // If it's a new autoScouter, that's a meaningful change
      if (!oldAs) {
        return true;
      }

      // Check for status change
      if (oldAs.status !== newAs.status) {
        return true;
      }

      // Check the stage and review fields used by the step indicator.
      if (
        oldAs.crawlStatus !== newAs.crawlStatus ||
        oldAs.parseStatus !== newAs.parseStatus ||
        oldAs.matchStatus !== newAs.matchStatus ||
        oldAs.createStatus !== newAs.createStatus ||
        oldAs.processInProgress?.stage !== newAs.processInProgress?.stage ||
        oldAs.review?.url !== newAs.review?.url ||
        oldAs.review?.content !== newAs.review?.content ||
        oldAs.review?.matching !== newAs.review?.matching ||
        oldAs.dataOperationsError !== newAs.dataOperationsError
      ) {
        return true;
      }

      const oldFailedOperationCount = oldAs.dataOperations?.filter(operation => operation.status === 'failed').length || 0;
      const newFailedOperationCount = newAs.dataOperations?.filter(operation => operation.status === 'failed').length || 0;
      if (oldFailedOperationCount !== newFailedOperationCount) {
        return true;
      }

      // Check for changes in potentialSampleBallots count (for parsing stage)
      const oldPotentialCount = oldAs.potentialSampleBallots?.length || 0;
      const newPotentialCount = newAs.potentialSampleBallots?.length || 0;
      if (oldPotentialCount !== newPotentialCount) {
        return true;
      }

      // Check for changes in relevant sample ballots count
      const oldRelevantCount = oldAs.potentialSampleBallots?.filter(b => b.relevant === true).length || 0;
      const newRelevantCount = newAs.potentialSampleBallots?.filter(b => b.relevant === true).length || 0;
      if (oldRelevantCount !== newRelevantCount) {
        return true;
      }

      // Check for completed label matches while the transient consolidation
      // input is being rebuilt.
      const oldMatchedCount = [
        ...(oldAs.matchedLabels?.races || []),
        ...(oldAs.matchedLabels?.measures || [])
      ].filter(label => label.status === 'success').length;
      const newMatchedCount = [
        ...(newAs.matchedLabels?.races || []),
        ...(newAs.matchedLabels?.measures || [])
      ].filter(label => label.status === 'success').length;
      if (oldMatchedCount !== newMatchedCount) {
        return true;
      }

      // Check for changes in consolidatedContent (races/measures counts)
      const oldRaceCount = oldAs.consolidatedContent?.races?.length || 0;
      const newRaceCount = newAs.consolidatedContent?.races?.length || 0;
      const oldMeasureCount = oldAs.consolidatedContent?.measures?.length || 0;
      const newMeasureCount = newAs.consolidatedContent?.measures?.length || 0;
      if (oldRaceCount !== newRaceCount || oldMeasureCount !== newMeasureCount) {
        return true;
      }

    }

    // Check if any old autoScouters were removed
    if (oldAutoScouters.length !== newAutoScouters.length) {
      return true;
    }

    return false;
  }, []);

  const getAutoScouters = useCallback(async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const query: AutoScouterFindQuery = {
        electionKey: electionKey,
        $sort: { requestedAt: -1 },
        $limit: pageSize,
        $skip: (page - 1) * pageSize,
        ...getAutoScouterStateQuery(selectedState),
      };

      const response = await feathers.getService('auto-scouter').find({ query });
      const newAutoScouters = response.data;
      const newTotal = response.total;
      
      // If silent polling, check for meaningful changes before updating
      if (silent) {
        const hasMeaningfulChanges = hasStatusChanges(previousAutoScoutersRef.current, newAutoScouters);
        if (hasMeaningfulChanges) {
          setAutoScouters(newAutoScouters);
          setTotal(newTotal);
          previousAutoScoutersRef.current = newAutoScouters;
        }
      } else {
        setAutoScouters(newAutoScouters);
        setTotal(newTotal);
        previousAutoScoutersRef.current = newAutoScouters;
      }
    } catch (error) {
      console.error('Error fetching auto-scouters:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [feathers, electionKey, selectedState, page, pageSize, hasStatusChanges]);

  const updateUrlParams = (next: { state?: AutoScouterListState | null; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams(location.search);
    if (next.state !== undefined) {
      if (next.state) params.set('state', next.state);
      else params.delete('state');
      params.delete('status');
    }
    if (typeof next.page === 'number') {
      params.set('page', String(next.page));
    }
    if (typeof next.pageSize === 'number') {
      params.set('pageSize', String(next.pageSize));
    }
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const fetchDistricts = async (districtIds: string[]) => {
    if (districtIds.length === 0) {
      setDistricts([]);
      return;
    }
    const uniqueDistrictIds = [...new Set(districtIds)];
    try {
      const chunkSize = 20;
      const chunks: string[][] = [];
      for (let i = 0; i < uniqueDistrictIds.length; i += chunkSize) {
        chunks.push(uniqueDistrictIds.slice(i, i + chunkSize));
      }
      const responses = await Promise.all(chunks.map((chunk) => (
        feathers.getService('districts').find({ query: { _id: chunk }, $limit: chunkSize })
      )));
      const flattened = responses.flatMap((res) => res.data);
      setDistricts(flattened || []);
    } catch (error) {
      console.error('Error fetching district data:', error);
      setDistricts([]);
    }
  };

  const districtById = useMemo(() => {
    const map = new Map<string, District>();
    for (const d of districts || []) {
      if (d && d._id) map.set(d._id, d);
    }
    return map;
  }, [districts]);

  useEffect(() => {
    if (feathers && electionKey) {
      getAutoScouters();
    }
  }, [feathers, electionKey, selectedState, page, pageSize]);

  // Set up polling every 10 seconds
  useEffect(() => {
    if (!feathers || !electionKey) {
      return;
    }

    const intervalId = setInterval(() => {
      getAutoScouters(true); // Silent polling - only updates if meaningful changes detected
    }, 10000); // 10 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [feathers, electionKey, getAutoScouters]);

  useEffect(() => {
    if (autoScouters.length > 0 && feathers) {
      const districtIds = autoScouters
        .map(as => as.districtId)
        .filter((id): id is string => Boolean(id));
      fetchDistricts(districtIds);
    }
  }, [autoScouters, feathers]);

  const getConsolidatedContentSummary = (
    autoScouter: AutoScouter,
    consolidatedContent?: AutoScouter['consolidatedContent']
  ) => {
    const status = autoScouter.status;
    
    // For parsing stage: show count of potential sample ballots
    if (status === 'parsing') {
      const potentialCount = autoScouter.potentialSampleBallots?.length || 0;
      if (potentialCount > 0) {
        return `${potentialCount} potential sample ballot${potentialCount !== 1 ? 's' : ''} found`;
      }
      return '--';
    }
    
    // For matching stage: show relevant sample ballots with total races and measures
    if (status === 'matching') {
      const relevantCount = autoScouter.potentialSampleBallots?.filter(b => b.relevant === true).length || 0;
      const relevantUrls = new Set(
        (autoScouter.potentialSampleBallots || [])
          .filter(ballot => ballot.relevant === true)
          .map(ballot => ballot.url)
      );
      const relevantRawBallots = (autoScouter.sampleBallotsRaw || [])
        .filter(ballot => ballot.url && relevantUrls.has(ballot.url));
      
      // Sum parsed rows across the relevant source ballots.
      let totalRaces = 0;
      let totalMeasures = 0;
      
      relevantRawBallots.forEach(ballot => {
        totalRaces += ballot.races?.length || 0;
        totalMeasures += ballot.measures?.length || 0;
      });
      
      const parts: string[] = [];
      if (relevantCount > 0) {
        parts.push(`${relevantCount} relevant sample ballot${relevantCount !== 1 ? 's' : ''} found`);
      }
      if (totalRaces > 0 || totalMeasures > 0) {
        const items: string[] = [];
        if (totalRaces > 0) items.push(`${totalRaces} race${totalRaces !== 1 ? 's' : ''}`);
        if (totalMeasures > 0) items.push(`${totalMeasures} measure${totalMeasures !== 1 ? 's' : ''}`);
        if (items.length > 0) {
          parts.push(items.join(', '));
        }
      }
      
      return parts.length > 0 ? parts.join(' • ') : '--';
    }
    
    // For other stages, use the original logic if consolidatedContent exists
    if (!consolidatedContent) return null;
    
    const parts: string[] = [];
    
    if (consolidatedContent.sourceType) {
      const sourceTypeDisplay = consolidatedContent.sourceType === 'consolidated-sample-ballot' 
        ? 'Sample Ballot' 
        : consolidatedContent.sourceType === 'candidate-list'
        ? 'Candidate List'
        : consolidatedContent.sourceType;
      parts.push(`Source: ${sourceTypeDisplay}`);
    }
    
    const raceCount = consolidatedContent.races?.length || 0;
    const measureCount = consolidatedContent.measures?.length || 0;
    
    if (raceCount > 0 || measureCount > 0) {
      const items: string[] = [];
      if (raceCount > 0) items.push(`${raceCount} race${raceCount !== 1 ? 's' : ''}`);
      if (measureCount > 0) items.push(`${measureCount} measure${measureCount !== 1 ? 's' : ''}`);
      if (items.length > 0) {
        parts.push(items.join(', '));
      }
    }
    
    return parts.length > 0 ? parts.join(' • ') : null;
  };

  const handleCreateAutoScouter = async () => {
    if (!selectedDistrict || !electionKey) {
      return;
    }

    setCreating(true);
    try {
      feathers.getService('auto-scouter').create({
        electionKey,
        districtId: selectedDistrict._id,
      });
      
      // Close modal and reset form
      setCreateModalOpen(false);
      setSelectedDistrict(null);
      
      // Refresh the list
      await getAutoScouters();
    } catch (error) {
      console.error('Error creating auto-scouter:', error);
      // You might want to show an error message to the user here
    } finally {
      setCreating(false);
    }
  };

  if (!electionData && loading && autoScouters.length === 0) {
    return (
      <Wrapper>
        <LoadingTile>
          <span>Loading election data...</span>
          <LoadingSpinner width={64} />
        </LoadingTile>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` }}}
        navTree={[
          {
            text: 'Elections',
            to: { pathname: `/elections` }
          },
          {
            text: electionData?.name || 'Election',
            to: { pathname: `/elections/${electionKey}` }
          },
          { text: 'Auto Scouters' }
        ]}
        MoreButton={[
          {
            text: 'Trigger New Auto Scout',
            onClick: () => setCreateModalOpen(true),
            icon: <AutoAwesomeIcon fontSize="small" />
          }
        ]}
      />
      <Content>
        <Controls>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FilterButton
              label="State"
              value={selectedState}
              options={autoScouterStateOptions}
              onChange={(value: AutoScouterListState | null) => {
                setSelectedState(value);
                setPage(1);
                updateUrlParams({ state: value, page: 1 });
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small">
              <InputLabel id="page-size-label">Rows</InputLabel>
              <Select
                labelId="page-size-label"
                value={pageSize}
                label="Rows"
                onChange={(e) => {
                  const nextSize = Number(e.target.value) || 25;
                  setPageSize(nextSize);
                  setPage(1);
                  updateUrlParams({ pageSize: nextSize, page: 1 });
                }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Controls>
        <TableHeader>
          <Typography variant="h5">District</Typography>
          <Typography variant="h5">Status</Typography>
          <Typography variant="h5">Time Run</Typography>
          <Typography variant="h5">Consolidated Content Summary</Typography>
        </TableHeader>

        {loading ? (
          <LoadingTile>
            <span>Loading auto-scouters...</span>
            <LoadingSpinner width={64} />
          </LoadingTile>
        ) : autoScouters.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No auto-scouters found for this election.
            </Typography>
          </div>
        ) : (
          autoScouters.map((autoScouter) => {
            const district = districtById.get(autoScouter.districtId);
            const districtDisplay = district?.longName || district?.name || autoScouter.districtId || 'Unknown';
            const consolidatedContentSummary = getConsolidatedContentSummary(autoScouter, autoScouter.consolidatedContent);
            const createFailed = hasCreateError(autoScouter);
            
            return (
              <TableRowLink 
                key={autoScouter._id}
                to={`/elections/${electionKey}/auto-scouters/${autoScouter._id}`}
              >
                <div>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    {districtDisplay}
                  </Typography>
                </div>
                
                <StatusCell>
                  <StepIndicator steps={getAutoScouterSteps(autoScouter)} />
                  {createFailed && (
                    <CreateErrorWarning>
                      <WarningAmberIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                      <span>Creation encountered an error. Open to review and retry.</span>
                    </CreateErrorWarning>
                  )}
                </StatusCell>
                
                <Typography variant="body2">
                  {autoScouter.requestedAt 
                    ? moment(autoScouter.requestedAt instanceof Date ? autoScouter.requestedAt : new Date(autoScouter.requestedAt)).format('MMM D, YYYY h:mm A')
                    : '--'
                  }
                </Typography>
                
                <Typography variant="body2" color="textSecondary">
                  {consolidatedContentSummary || '--'}
                </Typography>
              </TableRowLink>
            );
          })
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #EEEEEE' }}>
          <Typography variant="body2" color="textSecondary">
            {total > 0 ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} of ${total}` : '0'}
          </Typography>
          <Pagination
            color="primary"
            page={page}
            count={Math.max(1, Math.ceil((total || 0) / pageSize))}
            onChange={(_, value) => {
              setPage(value);
              updateUrlParams({ page: value });
            }}
            showFirstButton
            showLastButton
          />
        </div>
      </Content>

      <Dialog
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setSelectedDistrict(null);
        }}
        maxWidth="sm"
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle>Trigger Auto Scout</DialogTitle>
        <DialogContent style={{ overflow: 'visible' }}>
          <Box sx={{ paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Select a district to trigger a new auto scout for this election.
            </Typography>
            <FormControl fullWidth>
              <DistrictSelectInput
                value={selectedDistrict}
                descendantOf={electionData?.state}
                onChange={(e: { target: { value: District | null } }) => {
                  setSelectedDistrict(e.target.value);
                }}
                districtTypes={['county', 'city', 'state-school']}
                style={{ width: '100%', minWidth: '260px' }}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateModalOpen(false);
              setSelectedDistrict(null);
            }}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAutoScouter}
            variant="contained"
            disabled={!selectedDistrict || creating}
          >
            {creating ? 'Creating...' : 'Trigger Auto Scout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

export default AutoScouters;

